import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { t, pick } from "@/i18n";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Eye, Heart, Sparkles, Hand, ArrowRight } from "lucide-react";
import { awardPoints } from "@/lib/points";

const ICONS = { eye: Eye, heart: Heart, sparkles: Sparkles, hand: Hand };

export default function Study() {
  const { lang } = useLang();
  const [params, setParams] = useSearchParams();
  const [steps, setSteps] = useState([]);
  const [done, setDone] = useState([]);

  useEffect(() => {
    api.get("/content/steps").then((r) => setSteps(r.data));
    api.get("/progress").then((r) => setDone(r.data.steps_done || []));
    // Load once on mount — no reactive deps needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = params.get("step") || steps[0]?.id;
  const step = steps.find((s) => s.id === active);
  const Icon = step ? ICONS[step.icon] : Eye;
  const isDone = step ? done.includes(step.id) : false;

  const markDone = async () => {
    const { data } = await api.post("/progress/step", { step_id: step.id });
    setDone(data.steps_done);
    awardPoints(data);
    toast.success(t(lang, "completed"));
  };

  const idx = steps.findIndex((s) => s.id === active);
  const next = steps[idx + 1];

  if (!step) return <div>Loading…</div>;

  return (
    <div className="space-y-10">
      <div className="text-xs uppercase tracking-widest text-accent font-medium">{t(lang, "study")}</div>

      {/* Step nav */}
      <div className="flex flex-wrap gap-2">
        {steps.map((s, i) => (
          <button
            key={s.id}
            onClick={() => setParams({ step: s.id })}
            data-testid={`study-tab-${s.id}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${
              s.id === active
                ? "bg-primary text-primary-foreground border-primary"
                : "border-border hover:bg-secondary"
            }`}
          >
            {String(i + 1).padStart(2, "0")} · {pick(s.title, lang)}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-secondary text-primary flex items-center justify-center">
              <Icon className="w-6 h-6" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-medium">{pick(step.title, lang)}</h1>
              <p className="text-muted-foreground mt-1">{pick(step.subtitle, lang)}</p>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t(lang, "lesson")}</div>
            <p className="text-base leading-relaxed">{pick(step.lesson, lang)}</p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{t(lang, "key_points")}</div>
            <ul className="space-y-2">
              {pick(step.key_points, lang).map((kp) => (
                <li key={kp} className="flex gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-accent" />
                  <span>{kp}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex gap-3 pt-4">
            {!isDone ? (
              <Button data-testid="study-mark-done" onClick={markDone} className="rounded-full gap-2">
                <CheckCircle2 className="w-4 h-4" /> {t(lang, "mark_complete")}
              </Button>
            ) : (
              <Button variant="outline" disabled className="rounded-full gap-2">
                <CheckCircle2 className="w-4 h-4" /> {t(lang, "completed")}
              </Button>
            )}
            {next && (
              <Button
                variant="ghost"
                className="rounded-full gap-2"
                onClick={() => setParams({ step: next.id })}
                data-testid="study-next-step"
              >
                {t(lang, "next_step")} <ArrowRight className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="p-6 rounded-xl border border-border bg-card h-fit">
          <div className="font-heading text-lg font-medium">{t(lang, "exercises")}</div>
          <p className="text-sm text-muted-foreground mt-2">
            {lang === "es" ? "Practica lo aprendido con ejercicios interactivos." :
             lang === "it" ? "Pratica ciò che hai imparato con esercizi interattivi." :
             "Practice what you learned with interactive exercises."}
          </p>
          <Link to={`/app/exercises?step=${step.id}`}>
            <Button data-testid="study-goto-exercises" className="mt-4 w-full rounded-full">
              {t(lang, "exercises")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
